import type { TISAUser } from '@/types/tisa-coins';
import { computeStudentStats } from '@/lib/tisa-helpers';

interface Props {
  students: TISAUser[];
}

export function ClassOverviewSection({ students }: Props) {
  // Calculate class totals
  const classStats = students.reduce((totals, student) => {
    const stats = computeStudentStats(student);
    return {
      totalXP: totals.totalXP + stats.total_xp,
      totalCoins: totals.totalCoins + stats.total_wealth,
      totalBadges: totals.totalBadges + stats.badges_earned,
      totalQuests: totals.totalQuests + stats.quests_completed
    };
  }, { totalXP: 0, totalCoins: 0, totalBadges: 0, totalQuests: 0 });

  // Top performers
  const topXP = students
    .map(s => ({ student: s, xp: computeStudentStats(s).total_xp }))
    .sort((a, b) => b.xp - a.xp)
    .slice(0, 3);

  return (
    <div className="bg-gray-900/50 border-2 border-gold/30 rounded-xl p-6">
      <h2 className="text-xl font-cinzel font-bold text-gold mb-4">
        👥 Class Overview
      </h2>

      {/* Class Totals */}
      <div className="space-y-3 mb-6">
        <div className="bg-emerald/10 rounded-lg p-3 border border-emerald/30">
          <p className="text-xs text-emerald mb-1">Total Class XP</p>
          <p className="text-2xl font-orbitron font-bold text-white">{classStats.totalXP}</p>
        </div>
        <div className="bg-gold/10 rounded-lg p-3 border border-gold/30">
          <p className="text-xs text-gold mb-1">Total Class Wealth</p>
          <p className="text-2xl font-orbitron font-bold text-white">{classStats.totalCoins}</p>
        </div>
        <div className="bg-purple/10 rounded-lg p-3 border border-purple/30">
          <p className="text-xs text-purple mb-1">Total Badges</p>
          <p className="text-2xl font-orbitron font-bold text-white">{classStats.totalBadges}</p>
        </div>
      </div>

      {/* Top Performers */}
      <div className="pt-4 border-t border-gray-700/50">
        <p className="text-sm text-gray-400 mb-3">🏆 Top XP Earners</p>
        <div className="space-y-2">
          {topXP.map((entry, index) => (
            <div key={entry.student.id} className="flex items-center justify-between bg-black/30 rounded-lg p-2">
              <div className="flex items-center gap-2">
                <span className={`text-lg ${
                  index === 0 ? 'text-gold' :
                  index === 1 ? 'text-gray-400' :
                  'text-amber-700'
                }`}>
                  {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                </span>
                <p className="text-sm font-poppins text-white">{entry.student.name}</p>
              </div>
              <p className="text-emerald font-orbitron text-sm font-bold">{entry.xp} XP</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
